import { deepMerge } from '@mail-core/std';
import { DebugLogger } from 'lib/debugLogger';
import { features } from 'reactApp/mail-features';

export const preQuotaSizeLimit: number = features.getAsInt('pre-overquota-size-limit', 80);
export const preQuotaEnablePaid = features.has('pre-overquota-size-enable-paid');

export const preOverquotaDebug = features.has('pre-overquota-debug');
export const preOverquotaDebugLogger = new DebugLogger({ DEBUG_SCOPE: 'PreOverquotaDebug', DEBUG: preOverquotaDebug });

export const preOverquotaExpGroup = features.getAsString('pre-overquota-exp-group');

type ButtonConfig<T extends string> = {
    enable: boolean;
    name: string;
    mode: T;
};

type PrimaryButtonMode = 'landing' | 'paywall';
type SecondaryButtonMode = 'cleaner' | 'none';
type PrimaryButtonConfig = ButtonConfig<PrimaryButtonMode>;
type SecondButtonConfig = ButtonConfig<SecondaryButtonMode>;

interface PreOverquotaConfigRaw {
    primaryButton?: Partial<PrimaryButtonConfig>;
    secondButton?: Partial<SecondButtonConfig>;
    modal: {
        enable: boolean;
        period: number;
        maxShowCount: number;
        primaryButton?: Partial<PrimaryButtonConfig>;
        secondButton?: Partial<SecondButtonConfig>;
    };
    banner: {
        enable: boolean;
        period: number;
        maxShowCount: number;
        primaryButton?: Partial<PrimaryButtonConfig>;
        secondButton?: Partial<SecondButtonConfig>;
        closable?: boolean;
    };
}

type PreOverquotaConfigPartial = Partial<PreOverquotaConfigRaw>;

const defaultPreOverquotaConfig = {
    primaryButton: {
        enable: true,
        name: 'Увеличить место',
        mode: 'landing' as PrimaryButtonMode,
    },
    secondButton: {
        enable: true,
        name: 'Буду знать',
        mode: 'none' as SecondaryButtonMode,
    },
    modal: {
        enable: false,
        period: 1,
        maxShowCount: 9999,
    },
    banner: {
        enable: false,
        period: 1,
        maxShowCount: 9999,
        closable: false,
    },
};

const preOverquotaCommunicationConfigRaw: PreOverquotaConfigPartial = features.getAsJSONWithDefaults(
    'pre-overquota-communication-config',
    defaultPreOverquotaConfig
);

interface PreOverquotaConfig {
    modal: {
        enable: boolean;
        period: number;
        maxShowCount: number;
        primaryButton: PrimaryButtonConfig;
        secondButton: SecondButtonConfig;
    };
    banner: {
        enable: boolean;
        period: number;
        maxShowCount: number;
        primaryButton: PrimaryButtonConfig;
        secondButton: SecondButtonConfig;
        closable: boolean;
    };
}
export const getPreOverquotaCommunicationConfig = (
    defaultConfig: typeof defaultPreOverquotaConfig,
    preOverquotaConfigRaw: PreOverquotaConfigPartial
) => {
    const config = deepMerge(defaultConfig, preOverquotaConfigRaw) as PreOverquotaConfigRaw;

    config.modal.primaryButton = deepMerge(
        defaultConfig.primaryButton,
        config.modal.primaryButton || config.primaryButton || {}
    ) as PrimaryButtonConfig;
    config.modal.secondButton = deepMerge(
        defaultConfig.secondButton,
        config.modal.secondButton || config.secondButton || {}
    ) as SecondButtonConfig;
    config.banner.primaryButton = deepMerge(
        defaultConfig.primaryButton,
        config.banner.primaryButton || config.primaryButton || {}
    ) as PrimaryButtonConfig;
    config.banner.secondButton = deepMerge(
        defaultConfig.secondButton,
        config.banner.secondButton || config.secondButton || {}
    ) as SecondButtonConfig;

    return config as PreOverquotaConfig;
};

const preOverquotaCommunicationConfig = getPreOverquotaCommunicationConfig(defaultPreOverquotaConfig, preOverquotaCommunicationConfigRaw);

export const preOverquotaModalConfig = preOverquotaCommunicationConfig.modal;
export const preOverquotaBannerConfig = preOverquotaCommunicationConfig.banner;

export const preOverquotaModalIgnoreShowLimits: boolean = features.has('pre-overquota-modal-ignore-show-limits');
export const preOverquotaBannerIgnoreShowLimits: boolean = features.has('pre-overquota-banner-ignore-show-limits');

// Описание – reactApp/appHelpers/featuresHelpers/features/docs/overquotaPre.md
